import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Bank, Buildings, ChartLineUp, Scales, Sparkle, MapTrifold, Scroll, UserCircle, Calculator, PlayCircle, ShieldCheck, MapPin } from "@phosphor-icons/react";
import SeeItInAction from "../components/landing/SeeItInAction";
import LandingVideoTour from "../components/landing/LandingVideoTour";
import FAQAccordion from "../components/landing/FAQAccordion";
import EstimaBot from "../components/EstimaBot";
import useDocumentMeta, { META } from "../lib/useDocumentMeta";

const HERO_IMG =
  "https://images.unsplash.com/photo-1774415108809-87df103ee55f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9vZHklMjBtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGV2ZW5pbmd8ZW58MHx8fHwxNzc2NTQyMTkyfDA&ixlib=rb-4.1.0&q=85";

const features = [
  {
    icon: <Buildings size={22} weight="duotone" />,
    title: "Property ledger",
    body: "Catalogue every flat, villa, and plot — with price, carpet area, loan terms and your gut-feel scores. Status-tracked across Evaluating, Owned, Sold.",
  },
  {
    icon: <Scales size={22} weight="duotone" />,
    title: "Side-by-side scoring",
    body: "Weighted decision score across location, commute, resale and price/value. Share a read-only link with family. CSV + PDF export.",
  },
  {
    icon: <Calculator size={22} weight="duotone" />,
    title: "15 calculators incl. Rental Snowball",
    body: "EMI · Rent vs Buy · Car vs Property · UC expected value · Leverage Optimizer · Wealth narrative · Prepay vs Invest — and our new Rental Snowball that simulates buying 3-5 flats from a single OD pot with XIRR on every rupee.",
  },
  {
    icon: <Bank size={22} weight="duotone" />,
    title: "Loan Rebalancer",
    body: "All your home, car, personal loans and overdrafts in one place. Upload them from Excel, split a bonus across loans, test part-payments, re-run your EMI after an RBI rate change, track progress with custom charts and PDF/Excel reports — plus AI repayment plans where Estima's calculator checks every rupee.",
  },
  {
    icon: <ChartLineUp size={22} weight="duotone" />,
    title: "Portfolio vs markets",
    body: "Own it? Watch your property compound against Nifty, MF, gold, silver, FD. Realized P&L on every flip.",
  },
  {
    icon: <MapTrifold size={22} weight="duotone" />,
    title: "Upcoming projects radar",
    body: "Curated launches across BLR / MUM / NCR / HYD / PUN / CHN. Star-watch the ones you care about; they follow you to your Dashboard.",
  },
  {
    icon: <MapPin size={22} weight="duotone" />,
    title: "Nearby market data",
    body: "Real rent and ₹/sqft around every owned property — Estima's city presets + community contributions. 1-click rental listings that deep-link to 99acres, MagicBricks, NoBroker & more.",
  },
  {
    icon: <UserCircle size={22} weight="duotone" />,
    title: "Tenants + rent portal",
    body: "Track tenants, generate PDF receipts, and invite them to a Razorpay-powered portal. They pay; Estima auto-generates the receipt.",
  },
  {
    icon: <ShieldCheck size={22} weight="duotone" />,
    title: "Aadhaar verification",
    body: "Verify tenants and Will-witnesses with an Aadhaar OTP flow (UIDAI-contract-compatible stub today; real partner swap when you're ready).",
  },
  {
    icon: <Scroll size={22} weight="duotone" />,
    title: "AI Will · Lawyer marketplace · Witness e-sign",
    body: "Let AI draft a property-level distribution. Then book a verified family-law counsel from Estima's panel (pay via Razorpay, 10% platform fee), collect Aadhaar-backed witness e-signatures, and email each beneficiary a password-protected PDF.",
  },
  {
    icon: <Sparkle size={22} weight="duotone" />,
    title: "AI advisor",
    body: "Claude Sonnet 4.5 reads your numbers and returns a 2-line verdict, 5 bullets of reasoning, and one concrete next step.",
  },
  {
    icon: <PlayCircle size={22} weight="duotone" />,
    title: "One-click demo mode",
    body: "Kick the tires with no signup. Pre-seeded with sample properties, tenants and receipts.",
  },
];

export default function Landing() {
  useDocumentMeta(META.landing);
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="border-b hairline">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="h-8 w-8 border hairline flex items-center justify-center">
              <span className="font-serif text-xl leading-none">E</span>
            </div>
            <span className="font-serif text-2xl tracking-tight">Estima</span>
          </Link>
          <nav className="flex items-center gap-6">
            <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition">
              Features
            </a>
            <a href="#how" className="text-sm text-muted-foreground hover:text-foreground transition">
              How it works
            </a>
            <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition" data-testid="nav-pricing-link">
              Pricing
            </Link>
            <Link
              to="/login"
              className="text-sm text-muted-foreground hover:text-foreground transition"
              data-testid="nav-login-link"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="btn-primary text-sm px-4 py-2"
              data-testid="nav-register-cta"
            >
              Start free
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b hairline">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(0.35) saturate(1.1)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background" />
        <div className="relative max-w-7xl mx-auto px-6 py-28 lg:py-36">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div className="eyebrow mb-6" data-testid="hero-eyebrow">
              A property decision engine · built for Indian buyers
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground">
              Buy the right home,
              <br />
              <span className="italic text-[hsl(var(--secondary))]">not the shiny one.</span>
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Estima puts every flat, villa and plot you&apos;re eyeing on a single scorecard —
              then stress-tests each against renting, mutual funds and equity. You ship a
              decision, not a spreadsheet.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <Link
                to="/register"
                className="btn-primary px-6 py-3 inline-flex items-center gap-2 text-sm"
                data-testid="hero-cta-register"
              >
                Build your first comparison <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/login"
                className="btn-ghost px-6 py-3 text-sm"
                data-testid="hero-cta-login"
              >
                I already have an account
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { k: "₹85L+", v: "avg. decision value" },
            { k: "4-way", v: "comparison matrix" },
            { k: "<60s", v: "to a verdict" },
            { k: "0%", v: "builder bias" },
          ].map((s) => (
            <div key={s.v}>
              <div className="num-metric text-3xl md:text-4xl text-foreground">{s.k}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-2">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* See it in action — feature walkthroughs from the demo account */}
      <SeeItInAction />

      <LandingVideoTour />

      {/* What's New */}
      <section id="whats-new" className="border-b hairline">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
            <div>
              <div className="eyebrow mb-2">What's new</div>
              <h2 className="font-serif text-3xl sm:text-4xl">Shipped in the last 30 days.</h2>
            </div>
            <div className="text-xs text-muted-foreground">Updated regularly — every release.</div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { date: "Jun", tag: "Loans · AI", title: "AI loan repayment suggestions", body: "Answer 5 quick questions and pick an AI model. You get a repayment plan, and Estima's own calculator rechecks every rupee, lock-in and prepayment charge before you see it." },
              { date: "Jun", tag: "Loans", title: "Custom loan charts", body: "Line, area, stacked, grouped, combo or donut charts. Pick loans, timeframe (monthly, CY, FY, custom), principal vs interest, rupees or % of EMI." },
              { date: "Jun", tag: "Loans", title: "Progress & Reports", body: "See what you've paid and what's left for each loan or all loans together, as on any date. Download a payment report as PDF or Excel." },
              { date: "Jun", tag: "Loans", title: "Rate Change Recalculator", body: "Bank changed your rate, or RBI moved the repo rate? Compare Keep EMI, Keep tenure or a custom EMI, see the impact, and undo anytime." },
              { date: "Jun", tag: "Loans", title: "Prepayment Simulator + Bonus Advisor", body: "Test part-payments on any loan and save up to 10 scenarios. Got a bonus? Estima shows the best way to split it across your loans." },
              { date: "Jun", tag: "Loans", title: "Bulk loan upload", body: "Download the Excel template, fill in up to 25 loans, upload — your whole loan list is ready in seconds." },
            ].map((n) => (
              <div key={n.title} className="card-flat p-6 hover:border-[hsl(var(--secondary))] transition group">
                <div className="flex items-center justify-between mb-3 text-xs">
                  <span className="text-[hsl(var(--secondary))] uppercase tracking-wider">{n.tag}</span>
                  <span className="text-muted-foreground">{n.date}</span>
                </div>
                <div className="font-serif text-xl mb-2 group-hover:text-[hsl(var(--secondary))] transition">{n.title}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{n.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <div className="eyebrow mb-4">What Estima does</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight">
            A single place where real estate meets the rest of your portfolio.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-[hsl(var(--border))] border hairline">
          {features.map((f) => (
            <div
              key={f.title}
              className="bg-card p-8 md:p-10"
              data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="text-[hsl(var(--secondary))] mb-6">{f.icon}</div>
              <h3 className="font-serif text-2xl mb-3">{f.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t hairline bg-[hsl(var(--muted))]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16">
            <div>
              <div className="eyebrow mb-4">The method</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-8">
                Numbers first. Vibes second.
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Every home you consider is scored on six axes and six financial scenarios.
                Nothing is hidden behind jargon — every assumption is editable, every chart is
                explained.
              </p>
            </div>
            <ol className="space-y-6">
              {[
                ["01", "Add the properties", "Price, area, loan, expected appreciation and rental yield."],
                ["02", "Score the intangibles", "Location, amenities, safety, commute, resale — 0 to 10."],
                ["03", "Run the comparisons", "Rent vs Buy. Property vs Mutual Funds vs Equity. EMI schedules."],
                ["04", "Plan your loans", "Add or bulk-upload every loan. Simulate prepayments, split a bonus, react to rate changes, and track progress with charts and reports."],
                ["05", "Ask the advisor", "AI verdicts and loan repayment plans grounded in your actual numbers — every figure rechecked by Estima's calculator."],
              ].map(([n, t, b]) => (
                <li key={n} className="flex gap-6 border-b hairline pb-6">
                  <div className="num-metric text-3xl text-[hsl(var(--secondary))] w-16 shrink-0">
                    {n}
                  </div>
                  <div>
                    <div className="font-serif text-xl mb-1">{t}</div>
                    <div className="text-sm text-muted-foreground">{b}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Why buy teaser */}
      <section className="border-t hairline bg-[hsl(var(--muted))]/40">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-12 items-center">
            <div>
              <div className="eyebrow mb-4 text-[hsl(var(--secondary))]">Long-game math</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">Why buying still wins over 25 years.</h2>
              <p className="text-muted-foreground mt-5 leading-relaxed">
                Tier-1 land is finite. Rents compound at 8% a year. EMIs end at tenure — and every rupee after that becomes investable. And when you pass property on, India&apos;s capital-gains clock resets at zero. Run your own numbers in the Wealth narrative calculator.
              </p>
              <Link to="/register" className="btn-primary px-6 py-3 text-sm inline-flex items-center gap-2 mt-8" data-testid="wealth-landing-cta">
                Run the 25-year math <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { h: "Tier-1 land is finite", p: "Bengaluru, Mumbai, Delhi NCR, Hyderabad, Pune — a fixed-supply game with a growing buyer pool." },
                { h: "Rent compounds 8% a year", p: "₹30k today ≈ ₹2L in 25 years. That money never comes back." },
                { h: "EMI has an expiry date", p: "After year 20 every rupee that used to go to the bank compounds into your net worth." },
                { h: "Generational tax-free transfer", p: "Inheritance resets India's capital-gains clock to zero. Equity and MFs don't." },
              ].map((c) => (
                <div key={c.h} className="card-flat p-5" data-testid={`why-buy-${c.h.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}>
                  <div className="font-serif text-xl mb-2">{c.h}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{c.p}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t hairline">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6">
            Stop arguing with your spouse about spreadsheets.
          </h2>
          <p className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto">
            Create your account and model your first property in under two minutes.
          </p>
          <Link
            to="/register"
            className="btn-primary px-8 py-4 inline-flex items-center gap-2"
            data-testid="cta-register-bottom"
          >
            Get started · it&apos;s free <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <FAQAccordion />

      <footer className="border-t hairline">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-start justify-between gap-6 text-sm text-muted-foreground">
          <div className="max-w-xl space-y-2">
            <div>© {new Date().getFullYear()} Estima — a personal property decision engine. All rights reserved.</div>
            <p className="text-xs">
              Estima provides analytical tools and educational content only; nothing on this platform constitutes investment, legal, tax, or real-estate advice.
              Consult a SEBI-registered investment adviser, a chartered accountant, or a licensed lawyer before acting on any output.
            </p>
          </div>
          <div className="flex gap-6 flex-wrap">
            <a href="#features" className="hover:text-foreground">
              Features
            </a>
            <a href="#how" className="hover:text-foreground">
              Method
            </a>
            <Link to="/pricing" className="hover:text-foreground">
              Pricing
            </Link>
            <Link to="/legal/terms" className="hover:text-foreground" data-testid="landing-terms-link">
              Terms
            </Link>
            <Link to="/legal/privacy" className="hover:text-foreground" data-testid="landing-privacy-link">
              Privacy
            </Link>
            <Link to="/disclaimer" className="hover:text-foreground" data-testid="landing-disclaimer-link">
              Disclaimer
            </Link>
            <Link to="/login" className="hover:text-foreground">
              Sign in
            </Link>
          </div>
        </div>
      </footer>
      <EstimaBot page="landing" />
    </div>
  );
}
